import { Component, computed, inject } from '@angular/core';
import { DataOptions, DataResponse, DataService, ExportDataOptions, FilterExpression } from '../../services/data.service';
import { last, range, sum } from 'lodash-es';
import {
	BARCHART_COLUMN_NAME,
	ColumnType,
	createMeasureColumn,
	DataRow,
	DUMMY_ROW_ID,
	getInitialAttributes,
	getSubgroupAttributes,
} from '../../shared/dashboard/data-tree-table/data-tree-table';
import { Observable } from 'rxjs';
import { ColumnDef, createColumnDef, createDefaultHeaderCellDef } from '../../shared/components/table/table/table.model';
import { att } from '../../services/measures';
import { getLeafA, Level, Path } from '../../services/data-tree';
import { HbarPartitionComponent } from '../../shared/components/hbar/hbar-partition.component';
import { BarInfo } from '../../services/stacked-bars';
import { maxOver, noAgg0, xAgg0 } from '../../services/aggregation';
import { generateCssClassForLesregistratie, generateCssClassForString } from '@cumlaude/shared-utils';
import { LabelCellComponent } from '../../shared/components/table/cells/label-cell/label-cell.component';
import { formatNumber } from '@angular/common';
import { PivotTableConfig } from '../../shared/dashboard/pivot-table/pivot-table-config';
import { Attributes, SelectionConfig } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext, DataRequestContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { AttrPath, Table } from '@cumlaude/metadata';
import { DashboardVariant, Interval } from '../../services/weergave-opties';
import { getCellPath, getInitialAttributesForCell, PivotTableComponent } from '../../shared/dashboard/pivot-table/pivot-table.component';
import { LinechartTableConfig } from '../../shared/dashboard/linechart-table/linechart-table-config';
import { PointInfo } from '../../shared/dashboard/linechart-table/linechart/linechart.component';
import { Axis, createYAxis } from '../../services/axis';
import { formatDecimal } from '@cumlaude/shared-pipes';
import { ExportType } from '../../services/export.service';
import { LinechartTableComponent } from '../../shared/dashboard/linechart-table/linechart-table.component';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { WeergaveOptieComponent } from '../../shared/components/weergave-optie/weergave-optie.component';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

interface LesregistratiesI extends Attributes {
	lr_nm_lesregistratie: string;
	lr_m_nr_leerlingen: number;
	lr_m_nr_lesregistraties: number; // aantal unieke lesregistraties
	xa: { [nr: number]: { lr_m_nr_lesregistraties: number } };
}

interface LesregistratiesA extends Attributes {
	aantalLesregistraties: number;
	aantalLesregistraties_x: number;
	maxLesregistraties: number;
	aantalLeerlingen: number;
}

@Component({
	selector: 'app-lesregistraties',
	templateUrl: './lesregistraties.component.html',
	styleUrls: ['./lesregistraties.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		PivotTableComponent,
		LinechartTableComponent,
		FilterBarComponent,
		WeergaveOptieComponent,
		LegendaComponent,
		ToggleButtonsComponent,
	],
})
export class LesregistratiesComponent extends PivotTableConfig<LesregistratiesI, LesregistratiesA> {
	protected readonly dataService = inject(DataService);

	actueelFilters: FilterName[] = [
		'lr_d_datum.per_nm_schooljaar',
		'lr_fk_lb.lb_co_brin',
		'lr_fk_vs.vs_nm_vestiging',
		'lr_fk_ilt.ilt_nm_niveau',
		'lr_nr_leerjaar',
		'lr_is_geoorloofd',
		'lr_nm_klas',
		'lr_nm_lesregistratie',
		'lr_nm_vak',
		'lr_d_datum.per_d_datum',
	];

	historieFilters: FilterName[] = [
		'lr_d_datum.per_nm_schooljaar', // is onzichtbaar en filtert niet, maar is alleen om het schooljaar van de actueel-variant vast te houden
		'x_lr_schooljaar_historie',
		'x_lr_multiselect_schooljaar',
		'lr_fk_ilt.ilt_nm_niveau',
		'lr_is_geoorloofd',
		'lr_nr_leerjaar',
		'lr_nm_klas',
		'lr_nm_lesregistratie',
		'lr_nm_vak',
		'lr_fk_vs.vs_nm_vestiging',
		'lr_d_datum.per_d_datum',
	];

	protected actueelExportTypes = Object.values(ExportType);
	protected historieExportTypes = [ExportType.AFBEELDING, ExportType.PDF, ExportType.DATA];

	fixedSubgroups: AttrPath[] = [['lr_nm_lesregistratie']];

	readonly intervalSubgroups = computed<AttrPath[]>(() => {
		switch (this.interval()) {
			case Interval.MAAND:
				return [
					['lr_d_datum', 'per_nm_schooljaar'],
					['lr_d_datum', 'per_nr_maand'],
				];
			case Interval.SCHOOLJAAR:
				return [['lr_d_datum', 'per_nm_schooljaar']];
			case Interval.WEEK:
				return [
					['lr_d_datum', 'per_nm_schooljaar'],
					['lr_d_datum', 'per_nr_isoweek'],
				];
		}
	});

	readonly nrIntervalSubgroups = computed(() => <1 | 2>this.intervalSubgroups().length);

	readonly historieSubgroups = computed(() => [...this.fixedSubgroups, ...this.intervalSubgroups()]);

	override availableGroups: AttrPath[] = [
		['lr_fk_ilt', 'ilt_nm_niveau'],
		['lr_fk_ilt', 'ilt_abb_profiel'],
		['lr_nr_leerjaar'],
		['lr_nm_klas'],
		['lr_fk_ll', 'll_nm_leerling'],
		['lr_is_geoorloofd'],
		['lr_des_lesregistratie'],
		['lr_nm_opleiding'],
		['lr_fks_mw', 'mw_nm_medewerker'],
		['lr_fk_vs', 'vs_nm_vestiging'],
		['lr_nm_vak'],
	];

	permanentFilterExpressions: FilterExpression[] = [];

	variant = this.qp.signal('variant');

	interval = this.qp.signal('interval');

	override singleAggregators = {
		aantalLesregistraties: noAgg0('lr_m_nr_lesregistraties'),
		maxLesregistraties: maxOver('lr_m_nr_lesregistraties'),
		aantalLeerlingen: noAgg0('lr_m_nr_leerlingen'),
		aantalLesregistraties_x: xAgg0('lr_m_nr_lesregistraties'),
	};

	factTable = Table.fac_lr_lesregistratie;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		const totaalLevel = this.groeperingService.huidigeAantalGroeperingen() + (this.fixedSubgroups.length - 1);
		return this.dataService.getLesregistratieData({ ...options, r: range(0, totaalLevel + 1), xa: [range(0, totaalLevel)] });
	}

	override getExportData(options: ExportDataOptions) {
		return this.dataService.getLesregistratieExportData(options);
	}

	override createMeasureColumns(
		context: DashboardContext<LesregistratiesI, LesregistratiesA, LesregistratiesComponent>
	): ColumnDef<DataRow<LesregistratiesA>>[] {
		return [
			createMeasureColumn('Aantal registraties', att('aantalLesregistraties'), { context }),
			this.createLeerlingColumn('Leerlingen', att('aantalLeerlingen'), { context }),
		];
	}

	override columnOrder: ColumnType[] = [ColumnType.GROUP, ColumnType.PIVOT, ColumnType.MEASURE];

	override createPivotColumns(
		columnRoot: Level<LesregistratiesA, number[]>,
		context: DashboardContext<LesregistratiesI, LesregistratiesA, LesregistratiesComponent>
	): ColumnDef<DataRow<LesregistratiesA>>[] {
		return columnRoot.r.map((path) => this.createPivotColumn(path, context));
	}

	private createPivotColumn(
		path: Path<LesregistratiesA, number[]>,
		context: DashboardContext<LesregistratiesI, LesregistratiesA, LesregistratiesComponent>
	): ColumnDef<DataRow<LesregistratiesA>> {
		const { subgroupNames, measureNames } = context;
		const attrs = getSubgroupAttributes<LesregistratiesI>(subgroupNames, path);
		const { lr_nm_lesregistratie } = attrs;
		const BARCHART_CLASS = 'cdk-column-' + BARCHART_COLUMN_NAME;

		const colIndex = last(path)!.i;
		const colKey = `lesregistratie-${colIndex}`;
		const coldef = createColumnDef<DataRow<LesregistratiesA>>(colKey);
		coldef.sortable = true;
		coldef.header = createDefaultHeaderCellDef(colKey, lr_nm_lesregistratie);
		coldef.header.class = BARCHART_CLASS;

		coldef.body.component = HbarPartitionComponent;
		coldef.body.class = BARCHART_CLASS;
		coldef.body.getValue = (row: DataRow<LesregistratiesA>) => {
			if (row._id === DUMMY_ROW_ID) {
				// wordt aangeroepen bij sorteren, geef hier de measure terug ipv BarInfo
				return sum(
					last(row._path)!.r.map(
						(path) =>
							getInitialAttributesForCell<LesregistratiesI, LesregistratiesA>(subgroupNames, measureNames, path, colIndex)
								?.lr_m_nr_lesregistraties ?? 0
					)
				);
			}

			const cellPath = getCellPath(row._path, colIndex);
			if (!cellPath) return { qty: null, stacks: [], axis: { min: 0, max: 100, ticks: [] } };

			const { lr_m_nr_lesregistraties, lr_nm_lesregistratie } = getInitialAttributes<LesregistratiesI>(subgroupNames, measureNames, cellPath);

			const maxSize = row._path[0].a.maxLesregistraties;
			const barInfo: BarInfo = {
				size: lr_m_nr_lesregistraties,
				selection: this.createSelection(context, cellPath),
				className: generateCssClassForLesregistratie(lr_nm_lesregistratie),
			};
			return {
				barWidth: 100,
				stacks: [[barInfo]],
				qty: lr_m_nr_lesregistraties,
				axis: { min: 0, max: maxSize, ticks: [] },
				text: formatNumber(lr_m_nr_lesregistraties, 'nl-NL', '1.0-0'),
			};
		};

		coldef.footer.component = LabelCellComponent; // Align left
		const footerVal = last(path)!.a.aantalLesregistraties_x;
		coldef.footer.getValue = () => formatNumber(footerVal, 'nl-NL', '1.0-0');
		coldef.footer.class = BARCHART_CLASS;

		return coldef;
	}

	linechartConfig = new (class extends LinechartTableConfig<LesregistratiesI, LesregistratiesA> {
		constructor(private readonly dashboard: LesregistratiesComponent) {
			super();
		}

		override makePoints(
			r: Path<LesregistratiesA, number[]>[],
			_context: DashboardContext<LesregistratiesI, LesregistratiesA, LinechartTableConfig<LesregistratiesI, LesregistratiesA>>,
			group: Level<LesregistratiesA, number[]>
		): PointInfo[] {
			const lineClass = generateCssClassForString(group.k);
			const qty = r.length ? getLeafA(r[0]).count_records : 0;
			return [
				{ lineClass, qty },
				{ lineClass: `${lineClass}-fill`, qty },
			];
		}

		override getYAxis(path: Path<LesregistratiesA, number[]>): Axis {
			return createYAxis([0, getLeafA(path).maxLesregistraties], 5, (val) => formatDecimal(val, '1.0-1'));
		}

		override getLineNames(path: Path<LesregistratiesA, number[]>): { [lineClass: string]: string } {
			const line = last(path)!.k!;
			return { [generateCssClassForString(line)]: line };
		}

		factTable = Table.fac_aw_aanwezigheid;
		getData = this.dashboard.getData.bind(this.dashboard);
		override getSelectionConfig = this.dashboard.getSelectionConfig.bind(this.dashboard);
		override dataRootSelection = this.dashboard.dataRootSelection;
		override getExportData = this.dashboard.getExportData.bind(this.dashboard);
		override singleAggregators = this.dashboard.singleAggregators;
		override availableGroups = this.dashboard.availableGroups;
		override enrichTableModel = this.dashboard.enrichTableModel.bind(this.dashboard);
	})(this);

	override getSelectionConfig(context: DataRequestContext<LesregistratiesA>): SelectionConfig<LesregistratiesA> | undefined {
		return { ...super.getSelectionConfig(context)!, getSize: att('aantalLeerlingen') };
	}

	protected readonly DashboardVariant = DashboardVariant;
	protected readonly Interval = Interval;
}
